import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import { OPTIONS } from 'adapter/in/common/widget/options';
import {
    chartLimitHaveOptionalData,
    mapChartChartLimitFieldData,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/ChartLimit';
import useArrayField, { RemoveElementRHF } from 'adapter/in/common/hooks/useArrayField';
import GaugeData from 'domain/entity/widgets/GaugeData';
import { pipe } from 'fp-ts/lib/function';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { usePrevious } from 'adapter/in/common/hooks/usePrevious';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import { WidgetType } from 'domain/entity/widget';
import { useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { ChartLimitValue, ConfigFieldType, FieldStatus } from 'adapter/in/common/form/types';

const DEFAULT_COLOR = '#000000';

export type ItemChartLimitValue = ChartLimitValue & { id: string };

type useChartLimitContainerProps = {
    fields: ItemChartLimitValue[];
    showReset: boolean;
    handleRemoveLimit: (index?: RemoveElementRHF) => void;
    handleAddLimit: () => void;
    handleResetLimits: () => void;
};

const useChartLimitContainer = (name: string, data?: WidgetDataTypes): useChartLimitContainerProps => {
    const { fields, getValues, handleAddElement, handleRemoveElement } = useArrayField<ChartLimitValue>(name);
    const { watch, setValue, clearErrors } = useFormContext();
    const [showReset, setShowReset] = useState(false);
    const fieldsUpdated = watch(name);
    const { widgetId } = useCurrentWidgetConfiguration();
    const prevWidgetId = usePrevious(widgetId);
    useEffect(() => {
        if (fieldsUpdated !== undefined && data?.type === WidgetType.GAUGE) {
            const chartLimits = chartLimitHaveOptionalData((fieldsUpdated as unknown) as ChartLimitValue[]);
            setShowReset(chartLimits.length > 0 || (data !== undefined && fieldsUpdated.length !== (data as GaugeData).checkpoints.length));
        }
    }, [fieldsUpdated]);

    const handleResetLimits = useCallback(() => {
        const castFields = (fieldsUpdated as unknown) as ItemChartLimitValue[];
        castFields.forEach((_, index) => handleRemoveElement(index));
        if (data) {
            pipe(mapChartChartLimitFieldData({ isDefaultData: true, chartLimits: (data as GaugeData).checkpoints }), (values) =>
                setValue(OPTIONS.CHECKPOINTS, values)
            );
        } else {
            setValue(OPTIONS.CHECKPOINTS, []);
        }
        clearErrors();
    }, [fieldsUpdated]);

    const handleAddLimit = useCallback(() => {
        handleAddElement({
            name: buildTextInputFieldData(FieldStatus.INITIAL_VALUE, '', ConfigFieldType.OPTIONS, ''),
            color: buildTextInputFieldData(FieldStatus.HAS_VALUE, DEFAULT_COLOR, ConfigFieldType.OPTIONS, DEFAULT_COLOR),
            value: buildTextInputFieldData(FieldStatus.INITIAL_VALUE, '', ConfigFieldType.OPTIONS, ''),
        });
        clearErrors();
    }, [fieldsUpdated]);

    const handleRemoveLimit = useCallback((index?: RemoveElementRHF) => {
        handleRemoveElement(index);
        if (data) {
            const castFields = (getValues(OPTIONS.CHECKPOINTS) as unknown) as ChartLimitValue[];
            const filteredCheckpoints = castFields.map((chartLimit) => ({
                name: buildTextInputFieldData(
                    FieldStatus.HAS_VALUE,
                    chartLimit.name.initialValue,
                    ConfigFieldType.OPTIONS,
                    chartLimit.name.value
                ),
                color: buildTextInputFieldData(
                    FieldStatus.HAS_VALUE,
                    chartLimit.color.initialValue,
                    ConfigFieldType.OPTIONS,
                    chartLimit.color.value
                ),
                value: buildTextInputFieldData(
                    FieldStatus.HAS_VALUE,
                    String(chartLimit.value.initialValue),
                    ConfigFieldType.OPTIONS,
                    String(chartLimit.value.value)
                ),
            }));
            setValue(OPTIONS.CHECKPOINTS, filteredCheckpoints);
        }
        clearErrors();
    }, []);

    return {
        fields: widgetId === prevWidgetId ? (fields as ItemChartLimitValue[]) : [],
        showReset,
        handleAddLimit,
        handleResetLimits,
        handleRemoveLimit,
    };
};

export default useChartLimitContainer;
